import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { ArrowLeft, Upload, X, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { kbApi } from '@/api/kb';
import { KB_ACCEPTED_TYPES, KB_FORMATS_LABEL } from '@/lib/uploadFormats';

const MAX_FILES   = 50;
const MAX_SIZE_MB = 500;

export default function KnowledgeBaseCreate() {
  const navigate = useNavigate();
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [chunkSize, setChunkSize]     = useState(64000);
  const [files, setFiles]             = useState<File[]>([]);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');

  const onDrop = useCallback(
    (accepted: File[]) => setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES)),
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: KB_ACCEPTED_TYPES,
    maxSize: MAX_SIZE_MB * 1024 * 1024,
    maxFiles: MAX_FILES,
  });

  const removeFile = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!name.trim() || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      // 1. Create KB record
      const kb = await kbApi.create(name.trim(), description, chunkSize);

      // 2. Upload files (if any) — indexing runs in background on server
      if (files.length > 0) {
        await kbApi.uploadDocs(kb.id, files);
      }

      navigate(`/knowledge-base/${kb.id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* 頂部列 */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">建立新知識庫</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge-base')}>
          <ArrowLeft className="size-4 mr-1" />
          返回列表
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 space-y-5 shadow-sm">
        {/* 知識庫名稱 */}
        <div className="space-y-1">
          <Label htmlFor="kb-name">
            知識庫名稱 <span className="text-red-500">*</span>
          </Label>
          <Input
            id="kb-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：產品手冊、公司政策"
          />
        </div>

        {/* 描述 */}
        <div className="space-y-1">
          <Label htmlFor="kb-desc">描述</Label>
          <Textarea
            id="kb-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="簡要描述此知識庫的內容和用途。"
            rows={3}
          />
        </div>

        {/* 上傳區 */}
        <div className="space-y-2">
          <Label>上傳文件（可選，稍後也可新增）</Label>
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragActive
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary hover:bg-secondary'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="size-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {isDragActive ? '放開以上傳文件' : '拖放文件到這裡或點擊選擇'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              支援 {KB_FORMATS_LABEL}｜最多 {MAX_FILES} 個，總大小不超過 {MAX_SIZE_MB} MB
            </p>
          </div>

          {files.length > 0 && (
            <div className="space-y-1 mt-2">
              {files.map((file, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-secondary rounded px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="size-4 text-muted-foreground" />
                    <span className="text-foreground">{file.name}</span>
                    <span className="text-muted-foreground text-xs">({formatSize(file.size)})</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(i)}>
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 分段設定 */}
        <div className="space-y-1">
          <Label htmlFor="chunk-size">
            文本分段大小（Token） <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="chunk-size"
              type="number"
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              min={512}
              max={131072}
              step={512}
              className="w-28"
            />
            <span className="text-sm text-muted-foreground">tokens（建議 64000）</span>
          </div>
          <p className="text-xs text-muted-foreground">
            每個文件將依此大小切分後向量化。數字越大，每個區塊保留更多上下文，但搜尋精度略降。
          </p>
        </div>

        {/* 錯誤訊息 */}
        {error && (
          <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            ❌ {error}
          </p>
        )}

        {/* 按鈕列 */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => navigate('/knowledge-base')}
            disabled={submitting}
          >
            取消
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleSubmit}
            disabled={!name.trim() || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                建立中…
              </>
            ) : (
              '建立並上傳'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
